import dayjs from 'dayjs';
import { Order } from "../types";

export const languages = ["en", "de"];

export const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
};

export const extractEmailAddress = (address: string) => {
    const match = address.match(/<([^>]+)>/);
    if (match) {
        return match[1].toLowerCase();
    }
    return address.toLowerCase();
};

export function dedupeBy<T>(
    items: T[],
    keyFn: (item: T) => string
): T[] {
    const map = new Map<string, T>();

    for (const item of items) {
        map.set(keyFn(item), item); // last one wins
    }

    return Array.from(map.values());
}

//type Primitive = string | number | boolean | null | undefined | Date;

function flattenObject<T extends object>(obj: T, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};

    Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (Array.isArray(value)) {
            // Flatten individual items with indices
            value.forEach((item, index) => {
                if (typeof item === 'object' && item !== null) {
                    Object.assign(result, flattenObject(item, `${fullKey}.${index}`));
                } else {
                    result[`${fullKey}.${index}`] = String(item);
                }
            });

            // Add summarized field for common use
            if (value.length > 0 && typeof value[0] === 'object') {
                const ataList = value
                    .map(v => (v && typeof v === 'object' && 'ata' in v ? String(v.ata ?? '') : ''))
                    .filter(Boolean)
                    .join(', ');
                result[`${fullKey}.ataList`] = ataList;
            }
        } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
            Object.assign(result, flattenObject(value as object, fullKey));
        } else {
            result[fullKey] = value instanceof Date
                ? value.toISOString()
                : value !== null && value !== undefined
                    ? String(value)
                    : '';
        }
    });

    return result;
}

export function populateTemplate(template: string, order: Order): string {
    const flatOrder = flattenObject(order);

    // Step 1: Handle {{#each collection}}...{{/each}} loops
    template = template.replace(/{{#each\s+(\w+)}}([\s\S]*?){{\/each}}/g, (_, collectionKey: string, innerTemplate: string) => {
        const collection = (order as unknown as Record<string, unknown>)[collectionKey];
        if (!Array.isArray(collection)) return '';

        
        return collection.map((item) => {
            const itemFlat = flattenObject(item);
            return innerTemplate.replace(/{{(.*?)}}/g, (_, key: string) => {
                const trimmedKey = key.trim();
                return itemFlat[trimmedKey] || '';
            });
        }).join('');
    });

    // Step 2: Replace top-level {{placeholders}}
    return template.replace(/{{\s*([^{}\s]+)\s*}}/g, (_, key: string) => {
        const trimmedKey = key.trim();
        return flatOrder[trimmedKey] || '';
    });
}



export function saveCache<T>(key: string, data: T) {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
}

export function loadCache<T>(key: string, maxAgeMinutes: number): T | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        const age = (Date.now() - parsed.timestamp) / 1000 / 60;
        if (age <= maxAgeMinutes) return parsed.data as T;
    } catch {
        return null;
    }

    return null;
}


export const getPaymentColor = (o:Order) => {
    if (o.paid) return 'var(--color-green)';
    if (!o.paid && o.invoiced) return 'var(--color-orange)';
    if (!o.paid && !o.invoiced && o.prePaid) return 'var(--color-red)';
    return 'var(--color-blue)'; // not paid, not invoiced, not pre-paid
};

export const getPaymentCaption = (o: Order) => {
    if (o.paid) return 'Paid';
    if (!o.paid && o.invoiced) return 'Invoiced';
    if (!o.paid && !o.invoiced && o.prePaid) return 'Pre Paid';
    return 'Post Paid'; // not paid, not invoiced, not pre-paid
};

export enum CreatedOnFilter {
    All = "all",
    Today = "today",
    Yesterday = "yesterday",
    Tomorrow = "tomorrow",
    ThisWeek = "this_week",
    LastWeek = "last_week",
    NextWeek = "next_week",
    ThisMonth = "this_month",
    LastMonth = "last_month",
    Last30Days = "last_30_days",
    Last60Days = "last_60_days",
    Last90Days = "last_90_days",
    Next30Days = "next_30_days",
    Next60Days = "next_60_days"
}

// Build date range for a given filter selection
export function getCreatedOnRange(filter: CreatedOnFilter) {
    const now = dayjs();

    switch (filter) {
        case CreatedOnFilter.Today:
            return {
                start: now.startOf("day"),
                end: now.endOf("day"),
            };
        case CreatedOnFilter.Yesterday:
            return {
                start: now.subtract(1, "day").startOf("day"),
                end: now.subtract(1, "day").endOf("day"),
            };
        case CreatedOnFilter.Tomorrow:
            return {
                start: now.add(1, "day").startOf("day"),
                end: now.add(1, "day").endOf("day"),
            };
        case CreatedOnFilter.ThisWeek:
            return {
                start: now.startOf("week"),
                end: now.endOf("week"),
            };
        case CreatedOnFilter.LastWeek:
            return {
                start: now.subtract(1, "week").startOf("week"),
                end: now.subtract(1, "week").endOf("week"),
            };
        case CreatedOnFilter.NextWeek:
            return {
                start: now.add(1, "week").startOf("week"),
                end: now.add(1, "week").endOf("week"),
            };
        case CreatedOnFilter.ThisMonth:
            return {
                start: now.startOf("month"),
                end: now.endOf("month"),
            };
        case CreatedOnFilter.LastMonth:
            return {
                start: now.subtract(1, "month").startOf("month"),
                end: now.subtract(1, "month").endOf("month"),
            };
        case CreatedOnFilter.Last30Days:
            return {
                start: now.subtract(30, "day").startOf("day"),
                end: now.endOf("day"),
            };
        case CreatedOnFilter.Last60Days:
            return {
                start: now.subtract(60, "day").startOf("day"),
                end: now.endOf("day"),
            };
        case CreatedOnFilter.Last90Days:
            return {
                start: now.subtract(90, "day").startOf("day"),
                end: now.endOf("day"),
            };
        case CreatedOnFilter.Next30Days:
            return {
                start: now.startOf("day"),
                end: now.add(30, "day").endOf("day"),
            };
        case CreatedOnFilter.Next60Days:
            return {
                start: now.startOf("day"),
                end: now.add(60, "day").endOf("day"),
            };
        default:
            // 'All' or anything else => no date range
            return null;
    }
}

export function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


